const KEY = "igloobot.control.settings.v1";
const AUTH_KEY = "igloobot.control.auth.v1";

import type { ThemeMode } from "./theme.ts";

export type UserRole = "superadmin" | "admin" | "project_admin" | "user";

export type AuthUser = {
  id: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  globalRole: UserRole;
};

export type AuthState = {
  authToken: string | null;
  currentUser: AuthUser | null;
};

export type UiSettings = {
  gatewayUrl: string;
  token: string;
  sessionKey: string;
  lastActiveSessionKey: string;
  theme: ThemeMode;
  chatFocusMode: boolean;
  chatShowThinking: boolean;
  splitRatio: number; // Sidebar split ratio (0.4 to 0.7, default 0.6)
  navCollapsed: boolean; // Collapsible sidebar state
  navGroupsCollapsed: Record<string, boolean>; // Which nav groups are collapsed
  /** Combined agent/project context key: "agent:<id>" or "project:<uuid>" or "" */
  chatContextKey: string;
  pinnedProjectIds: string[];
  navWidth: number | null; // Persisted sidebar width (null = use CSS default 220px)
  sidebarMode: "coworker" | "notebook" | "legal" | "crm";
};

export function loadSettings(): UiSettings {
  const defaultUrl = (() => {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    // In dev the UI is served on its own Vite port (e.g. 5180), so same-origin
    // would point at Vite, not the gateway. Default to the local gateway WS
    // (19001) when served from localhost on any other port. Prod serves the UI
    // same-origin as the gateway, so location.host is correct there.
    if (location.hostname === "localhost" && location.port !== "19001") {
      return "ws://localhost:19001";
    }
    return `${proto}://${location.host}`;
  })();

  const defaults: UiSettings = {
    gatewayUrl: defaultUrl,
    token: "",
    sessionKey: "main",
    lastActiveSessionKey: "main",
    theme: "system",
    chatFocusMode: false,
    chatShowThinking: true,
    splitRatio: 0.6,
    navCollapsed: false,
    navGroupsCollapsed: {},
    chatContextKey: "",
    pinnedProjectIds: [],
    navWidth: null,
    sidebarMode: "coworker",
  };

  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as Partial<UiSettings>;
    return {
      gatewayUrl: (() => {
        const url = typeof parsed.gatewayUrl === "string" ? parsed.gatewayUrl.trim() : "";
        if (url && location.hostname !== "localhost" && (url.includes("localhost") || url.includes("127.0.0.1"))) {
          return defaults.gatewayUrl;
        }
        return url || defaults.gatewayUrl;
      })(),
      token: typeof parsed.token === "string" ? parsed.token : defaults.token,
      sessionKey:
        typeof parsed.sessionKey === "string" && parsed.sessionKey.trim()
          ? parsed.sessionKey.trim()
          : defaults.sessionKey,
      lastActiveSessionKey:
        typeof parsed.lastActiveSessionKey === "string" && parsed.lastActiveSessionKey.trim()
          ? parsed.lastActiveSessionKey.trim()
          : (typeof parsed.sessionKey === "string" && parsed.sessionKey.trim()) ||
            defaults.lastActiveSessionKey,
      theme:
        parsed.theme === "light" || parsed.theme === "dark" || parsed.theme === "system"
          ? parsed.theme
          : defaults.theme,
      chatFocusMode:
        typeof parsed.chatFocusMode === "boolean" ? parsed.chatFocusMode : defaults.chatFocusMode,
      chatShowThinking:
        typeof parsed.chatShowThinking === "boolean"
          ? parsed.chatShowThinking
          : defaults.chatShowThinking,
      splitRatio:
        typeof parsed.splitRatio === "number" &&
        parsed.splitRatio >= 0.4 &&
        parsed.splitRatio <= 0.7
          ? parsed.splitRatio
          : defaults.splitRatio,
      navCollapsed:
        typeof parsed.navCollapsed === "boolean" ? parsed.navCollapsed : defaults.navCollapsed,
      navGroupsCollapsed:
        typeof parsed.navGroupsCollapsed === "object" && parsed.navGroupsCollapsed !== null
          ? parsed.navGroupsCollapsed
          : defaults.navGroupsCollapsed,
      chatContextKey:
        typeof parsed.chatContextKey === "string" ? parsed.chatContextKey : defaults.chatContextKey,
      pinnedProjectIds:
        Array.isArray(parsed.pinnedProjectIds) && parsed.pinnedProjectIds.every((x) => typeof x === "string")
          ? parsed.pinnedProjectIds
          : defaults.pinnedProjectIds,
      navWidth:
        typeof parsed.navWidth === "number" && parsed.navWidth >= 180 && parsed.navWidth <= 520
          ? parsed.navWidth
          : defaults.navWidth,
      sidebarMode:
        parsed.sidebarMode === "coworker" || parsed.sidebarMode === "notebook" || parsed.sidebarMode === "legal" || parsed.sidebarMode === "crm"
          ? parsed.sidebarMode
          : defaults.sidebarMode,
    };
  } catch {
    return defaults;
  }
}

export function saveSettings(next: UiSettings) {
  localStorage.setItem(KEY, JSON.stringify(next));
}

// ---------- Auth persistence ----------

export function loadAuth(): AuthState {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) {
      return { authToken: null, currentUser: null };
    }
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (typeof parsed.authToken === "string" && parsed.currentUser) {
      return {
        authToken: parsed.authToken,
        currentUser: parsed.currentUser as AuthUser,
      };
    }
    return { authToken: null, currentUser: null };
  } catch {
    return { authToken: null, currentUser: null };
  }
}

export function saveAuth(state: AuthState) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(state));
}

export function clearAuth() {
  localStorage.removeItem(AUTH_KEY);
}
